import { Fragment, useState } from "react";
import { MessageSquare, SquareCheck } from "lucide-react";
import { CrmTask, priorityOptions, taskTypeOptions } from "types/crm/task";

const OVERDUE_PREVIEW_COUNT = 2;

const formatDueDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const labelFor = (options: { value: string; label: string }[], value?: string) =>
  options.find((o) => o.value === value)?.label ?? value;

const priorityBadgeClass: Record<string, string> = {
  HIGH: "bg-red-500/10 text-red-400",
  MEDIUM: "bg-amber-500/10 text-amber-400",
  LOW: "bg-emerald-500/10 text-emerald-400",
};

const OverdueTaskRow = ({
  task,
  onOpen,
}: {
  task: CrmTask;
  onOpen: (taskUuid: string) => void;
}) => {
  const owner = task.taskOwner?.name;
  const commentCount = task.commentCount ?? 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(task.uuid)}
      className="w-full flex items-start gap-3 rounded-lg border-l-[3px] border-l-red-500 bg-primary-surface px-3 py-3 text-left hover:brightness-110 transition"
    >
      <span className="p-1.5 rounded-lg shrink-0 bg-primary-variant">
        <SquareCheck className="w-4 h-4 text-color-accent" />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-sm text-neutral">{task.title}</p>
          {task.priority && (
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                priorityBadgeClass[task.priority] ?? "bg-primary-variant text-neutral-faded"
              }`}
            >
              {labelFor(priorityOptions, task.priority)}
            </span>
          )}
        </div>

        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-neutral-faded">
          {/* Dots go between the parts that are present, never before the first. */}
          {[
            task.dueAt && <span key="due">Due {formatDueDate(task.dueAt)}</span>,
            owner && <span key="owner">Assigned to {owner}</span>,
            task.taskType && (
              <span key="type">{labelFor(taskTypeOptions, task.taskType)}</span>
            ),
            commentCount > 0 && (
              <span key="comments" className="flex items-center gap-1 text-color-accent">
                <MessageSquare className="w-3 h-3" />
                {commentCount} {commentCount === 1 ? "comment" : "comments"}
              </span>
            ),
          ]
            .filter(Boolean)
            .map((part, index) => (
              <Fragment key={index}>
                {index > 0 && <span>•</span>}
                {part}
              </Fragment>
            ))}
        </p>
      </div>
    </button>
  );
};

interface OverdueSectionProps {
  tasks: CrmTask[];
  /** Opens a task in the Tasks tab. */
  onOpenTask: (taskUuid: string) => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

/**
 * Tasks past their due date and not yet done, pinned above the activity feed.
 * Shows the first two; the rest are a click away.
 */
const OverdueSection = ({
  tasks,
  onOpenTask,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: OverdueSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  if (!tasks.length) return null;

  const visible = expanded ? tasks : tasks.slice(0, OVERDUE_PREVIEW_COUNT);
  const hiddenCount = tasks.length - OVERDUE_PREVIEW_COUNT;

  return (
    <div>
      <h4 className="text-xs font-medium text-red-500 mb-2 uppercase tracking-wide">
        Overdue
      </h4>
      <div className="space-y-3">
        {visible.map((task) => (
          <OverdueTaskRow key={task.uuid} task={task} onOpen={onOpenTask} />
        ))}
      </div>

      <div className="mt-2 flex items-center gap-4">
        {!expanded && (hiddenCount > 0 || hasMore) && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs text-color-accent hover:underline"
          >
            {hasMore ? "Show more" : `Show ${hiddenCount} more`}
          </button>
        )}
        {expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-xs text-color-accent hover:underline"
          >
            Show less
          </button>
        )}
        {expanded && hasMore && onLoadMore && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingMore}
            className="text-xs text-color-accent hover:underline disabled:opacity-50"
          >
            {isFetchingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
};

export default OverdueSection;
